import React, { createContext, useState, useEffect, useRef } from 'react';
import { UserProvider } from './UserContext';
import { getTimeUntilExpiration, isTokenExpired } from '../utils/jwtUtils';


interface LoginResponse {
    success: boolean;
    loginForm: boolean;
    mfaEnabled: boolean;
    cookies?: string;
    user?: any;
    token?: string;
}

// Define the shape of the context value
interface AuthContextType {
    token: string | null;
    initialized: boolean;
    showRefreshOverlay: boolean;
    login: (username: string, password: string) => Promise<LoginResponse>;
    loginWithTFA: (username: string, password: string, tfaCode: string) => Promise<LoginResponse>;
    logout: () => void;
    isLoggedIn: () => boolean;
    refreshToken: () => void;
    triggerMicrosoftLogin: () => void;
}

// Create the context with a default value of `null`
export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<any> = ({ children }) => {
    // const [user, setUser] = useState<{ name: string } | null>(null);
    const [jwt_token, setToken] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);
    const [showRefreshOverlay, setShowRefreshOverlay] = useState(false);
    const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const refreshToken = async () => {
        try {
            const tokenResponse = await fetch('/api/users/refresh', { method: 'GET' });
            if (!tokenResponse.ok) {
                console.error('Token refresh failed');
                setShowRefreshOverlay(true);
                return;
            }
            const data = await tokenResponse.json();
            if (data.token) {
                setToken(data.token);
            } else {
                console.error('No token in refresh response');
                setShowRefreshOverlay(true);
            }
        } catch (error) {
            console.error('Error refreshing token:', error);
            setShowRefreshOverlay(true);
        }
    };

    const scheduleTokenRefresh = React.useCallback((token: string) => {
        // Clear any existing timeout
        if (refreshTimeoutRef.current) {
            clearTimeout(refreshTimeoutRef.current);
            refreshTimeoutRef.current = null;
        }

        // Check if token is already expired
        if (isTokenExpired(token)) {
            console.warn('Token is already expired');
            setShowRefreshOverlay(true);
            return;
        }

        // Get time until expiration
        const timeUntilExpiration = getTimeUntilExpiration(token);
        
        // Schedule refresh 1 minute (60000ms) before expiration
        const refreshBuffer = 60000; // 1 minute
        const refreshTime = timeUntilExpiration - refreshBuffer;

        if (refreshTime <= 0) {
            // Token expires in less than 1 minute, refresh immediately
            console.log('Token expires soon, refreshing immediately');
            refreshToken();
        } else {
            console.log(`Scheduling token refresh in ${Math.round(refreshTime / 1000)} seconds`);
            refreshTimeoutRef.current = setTimeout(() => {
                console.log('Automatic token refresh triggered');
                refreshToken();
            }, refreshTime);
        }
    }, []);

    const triggerMicrosoftLogin = () => {
        window.location.href = '/api/auth/ms-login';
    };

    // Check if the user is logged in on component mount
    useEffect(() => {
        const initUser = async () => {
            try {
                await refreshToken();
            } catch (error) {
                console.error('Error fetching user:', error);
            } finally {
                setInitialized(true); // Set initialized to true regardless of success
            }
        };
        initUser();

        // Cleanup timeout on unmount
        return () => {
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, []); // Empty dependency array ensures this runs only once on mount

    // Schedule token refresh when token changes
    useEffect(() => {
        if (jwt_token) {
            scheduleTokenRefresh(jwt_token);
        }
    }, [jwt_token, scheduleTokenRefresh]);

    const isLoggedIn = () => jwt_token !== null;

    const fetchCsrfToken = async () => {
        const response = await fetch('/api/users/me', { method: 'GET' }); // No auth header, will fail
        return (await response.json()).csrfToken;
    };

    const login = async (email: string, password: string) => {
        // setUser({ name: username });
        const csrfToken = await fetchCsrfToken();
        const response = await fetch('/api/users/login', {
            method: 'POST',
            body: JSON.stringify({ email, password, csrfToken }),
            headers: {
                'Content-Type': 'application/json',
            }
        });
        const data = await response.json() as LoginResponse;
        if (data.success) {
            setToken(data.token!);
        }
        return data as LoginResponse;
    }

    const loginWithTFA = async (username: string, password: string, tfaCode: string) => {
        const response = await fetch('/api/users/tfa', {
            method: 'POST',
            body: JSON.stringify({ username, password, token: tfaCode }),
            headers: {
                'Content-Type': 'application/json',
            }
        });
        const data = await response.json() as LoginResponse;
        if (data.success) {
            setToken(data.token!);
        }
        return data as LoginResponse;
    }

    const logout = async () => {
        const response = await fetch('/api/users/logout', { method: 'POST' });

        if (!response.ok) {
            console.error('Network response was not ok:', response.statusText);
        } else {
            setToken(null);
        }
    };

    return (
        <AuthContext.Provider value={{ 
            token: jwt_token, 
            initialized, 
            showRefreshOverlay, 
            login, 
            logout, 
            loginWithTFA, 
            isLoggedIn, 
            refreshToken,
            triggerMicrosoftLogin 
        }}>
            <UserProvider>
                {children}
            </UserProvider>
        </AuthContext.Provider>
    );
};